// Super-Admin only — manage the CompanyInfo table (the per-company branding +
// server-driven theme). List all companies, edit any, or add a new one. The
// three theme colours are editable with colour pickers; they're stored back in
// Flutter ARGB form ("0xFFRRGGBB") so both the React and Flutter apps read them.
//
// Endpoints: GetDataFromCompanyInfo (list) · AddCompanyInfo (row) ·
// UpdateCompanyInfo ({data, where}).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, AppBar, Toolbar, IconButton, Button, Card, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, Grid, Tooltip, Alert, Slider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import { getCompanies, addCompany, updateCompany } from '../../../core/api/api';
import { normalizeHex } from '../../../core/theme/buildTheme';
import { useApp } from '../../../core/state/AppContext';

// Text fields exposed in the editor (label + key).
const TEXT_FIELDS = [
  ['CompanyName', 'Company Name'],
  ['AssistanceContact', 'Assistance Contact'],
  ['CompanyEmailId', 'Email'],
  ['CompanyProp', 'Proprietor'],
  ['CompanyGstNum', 'GST Number'],
  ['CompanyAdd1', 'Address line 1'],
  ['CompanyAdd2', 'Address line 2'],
  ['CompanyCity', 'City'],
  ['CompanyState', 'State'],
  ['CompanyPincode', 'Pincode'],
  ['Theme', 'Theme'],
];
const COLOR_FIELDS = [
  ['BackColor', 'Background'],
  ['ButtonColor', 'Button'],
  ['FontColor', 'Font / Text'],
];

// <input type=color> requires a LOWERCASE #rrggbb value, else the browser
// treats it as invalid and resets the picker to black.
const toInput = (stored) => normalizeHex(stored, '#000000').toLowerCase();  // "0xFF8C8C8C" -> "#8c8c8c"
const toArgb = (hex) => `0xFF${normalizeHex(hex, '#000000').replace('#', '').toUpperCase()}`; // -> "0xFF8C8C8C"

const hexToRgb = (hex) => {
  const h = normalizeHex(hex, '#000000').replace('#', '');
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
};
const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
const rgbToHex = (r, g, b) =>
  `#${[r, g, b].map((n) => clamp(n).toString(16).padStart(2, '0')).join('')}`;

// A reliable colour field: swatch + native picker + R/G/B sliders + hex. The
// sliders are the dependable path — the native OS picker often doesn't report
// changes while you drag its wheel, so sliders/hex always update the value.
function ColorField({ label, value, onChange }) {
  const { r, g, b } = hexToRgb(value);
  const setChan = (key, val) => {
    const next = { r, g, b, [key]: val };
    onChange(rgbToHex(next.r, next.g, next.b));
  };
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={{ width: 26, height: 26, borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: value }} />
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }} color="text.primary">{label}</Typography>
        <input
          type="color" value={value}
          onInput={(e) => onChange(e.target.value)}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 36, height: 28, border: 'none', background: 'none', cursor: 'pointer' }}
        />
        <TextField
          variant="standard" value={value}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ style: { width: 78, fontSize: 12 } }}
        />
      </Box>
      <Stack direction="row" spacing={1.5}>
        {[['R', r, 'r', '#e53935'], ['G', g, 'g', '#43a047'], ['B', b, 'b', '#1e88e5']].map(([lbl, val, key, col]) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
            <Typography variant="caption" sx={{ width: 12 }} color="text.secondary">{lbl}</Typography>
            <Slider size="small" min={0} max={255} value={val} onChange={(_, v) => setChan(key, v)} sx={{ flex: 1, color: col }} />
            <Typography variant="caption" sx={{ width: 26, textAlign: 'right' }} color="text.primary">{val}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default function CompaniesScreen() {
  const navigate = useNavigate();
  const { user, refreshSession } = useApp();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCompanies();
      setList(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const blankForm = () => {
    const f = { CompanyId: null, AboutUs: '' };
    TEXT_FIELDS.forEach(([k]) => { f[k] = ''; });
    COLOR_FIELDS.forEach(([k], i) => { f[k] = ['#101820', '#37474f', '#ffffff'][i]; });
    return f;
  };

  const openNew = () => { setMsg(''); setForm(blankForm()); setOpen(true); };
  const openEdit = (c) => {
    setMsg('');
    const f = { CompanyId: c.CompanyId, AboutUs: c.AboutUs || '' };
    TEXT_FIELDS.forEach(([k]) => { f[k] = c[k] ?? ''; });
    COLOR_FIELDS.forEach(([k]) => { f[k] = toInput(c[k]); });
    setForm(f);
    setOpen(true);
  };

  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  const setColor = (k, v) => {
    // eslint-disable-next-line no-console
    console.log('[Companies] colour picked', k, '=', v);
    patch({ [k]: v });
  };

  const save = async () => {
    setSaving(true);
    setMsg('');
    const data = { AboutUs: form.AboutUs ?? '' };
    TEXT_FIELDS.forEach(([k]) => { data[k] = form[k] ?? ''; });
    COLOR_FIELDS.forEach(([k]) => { data[k] = toArgb(form[k]); });
    const editing = form.CompanyId != null && String(form.CompanyId) !== '';
    const where = `CompanyId = ${form.CompanyId}`;
    /* eslint-disable no-console */
    console.log(`[Companies] SAVE ${editing ? 'UPDATE ' + where : 'ADD'} | Back=${data.BackColor} Button=${data.ButtonColor} Font=${data.FontColor}`, data);
    try {
      const res = editing
        ? await updateCompany(data, where)
        : await addCompany(data);
      console.log('[Companies] SAVE response:', res && res.status, res && res.data);
      await load();
      const fresh = await getCompanies();
      const saved = (Array.isArray(fresh.data) ? fresh.data : [])
        .find((c) => String(c.CompanyId) === String(form.CompanyId));
      console.log('[Companies] row after save (colours):', saved && {
        BackColor: saved.BackColor, ButtonColor: saved.ButtonColor, FontColor: saved.FontColor,
      });
      // If we edited the current user's own company, refresh the session so the
      // new theme applies to the app immediately.
      const ownCompany = editing && String(form.CompanyId) === String(user?.CompanyInfoType);
      console.log('[Companies] editing own company?', ownCompany, 'my CompanyInfoType =', user?.CompanyInfoType);
      if (ownCompany) await refreshSession();
      setOpen(false);
    } catch (e) {
      console.error('[Companies] SAVE FAILED:', e && e.response && e.response.status, (e && e.response && e.response.data) || (e && e.message));
      setMsg('Save failed — see the browser console for details.');
    } finally {
      setSaving(false);
    }
    /* eslint-enable no-console */
  };

  const valid = form && String(form.CompanyName || '').trim().length > 0;

  return (
    <Box>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>Companies</Typography>
          <Tooltip title="Add company">
            <IconButton color="inherit" onClick={openNew}><AddIcon /></IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {list.map((c) => (
              <Grid item xs={12} sm={6} md={4} key={c.CompanyId}>
                <Card sx={{ p: 2, borderRadius: 3, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon color="inherit" />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={700} noWrap color="text.primary">{c.CompanyName || `Company ${c.CompanyId}`}</Typography>
                      <Typography variant="caption" color="text.secondary">ID {c.CompanyId} · {c.AssistanceContact || 'no contact'}</Typography>
                    </Box>
                    <IconButton onClick={() => openEdit(c)}><EditIcon /></IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                    {COLOR_FIELDS.map(([k, label]) => (
                      <Box key={k} sx={{ textAlign: 'center' }}>
                        <Box sx={{ width: 44, height: 24, borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: normalizeHex(c[k], '#000') }} />
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
          {list.length === 0 && (
            <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 6 }}>No companies found.</Typography>
          )}
        </Box>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form?.CompanyId != null ? 'Edit Company' : 'Add Company'}</DialogTitle>
        <DialogContent dividers>
          {form && (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              {msg && <Alert severity="error">{msg}</Alert>}
              {/* Colours */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Theme colours</Typography>
                {COLOR_FIELDS.map(([k, label]) => (
                  <ColorField key={k} label={label} value={form[k] || '#000000'} onChange={(v) => setColor(k, v)} />
                ))}
              </Box>

              {/* Text fields */}
              <Grid container spacing={1.5}>
                {TEXT_FIELDS.map(([k, label]) => (
                  <Grid item xs={12} sm={k === 'CompanyName' ? 12 : 6} key={k}>
                    <TextField
                      label={label} value={form[k] ?? ''} fullWidth size="small"
                      onChange={(e) => patch({ [k]: e.target.value })}
                    />
                  </Grid>
                ))}
              </Grid>

              <TextField
                label="About Us" value={form.AboutUs ?? ''} fullWidth multiline minRows={2}
                onChange={(e) => patch({ AboutUs: e.target.value })}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!valid || saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
